import React, { useState, useMemo } from 'react';
import { X, Plus, Trash2, TriangleAlert } from 'lucide-react';

// =============================================================================
// Capital › Programme › Items — what a project actually creates.
//
// A MODAL RATHER THAN COLUMNS ON THE GRID, and that is a layout decision made
// under pressure: the programme grid already carries twelve months plus five
// out-years plus opening plus totals. Adding a nested breakdown inline would
// push the months off screen, and the months are what the page is for.
//
// WHY BREAK A PROJECT DOWN AT ALL. Not mainly for the asset register — for
// DEPRECIATION. A project mixing a 15-year production line and a 5-year vehicle
// depreciates wrongly as one lump, and that error flows into the P&L, the cash
// flow add-back and the PPE balance. Each item carries its own class, so each
// depreciates on its own life.
//
// THE REMAINDER IS THE POINT OF THE DESIGN. Items are optional and do not have
// to add up: whatever they do not account for depreciates on the project's own
// class, exactly as the whole project did before items existed. So a
// half-finished breakdown is half-right rather than broken, and nothing is ever
// silently dropped.
//
// Site and cost centre are deliberately absent — both come from the project.
// An item at a different site is not a breakdown of that project, it is a
// second project.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white disabled:opacity-40 transition-colors';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString();

export default function CapexItemsModal({ project, assetClasses, onClose, onSave }) {
  const [items, setItems] = useState(
    (project.items || []).map((i, n) => ({ ...i, _key: i.capex_item_id || `n${n}` }))
  );

  const projectCost = (Number(project.opening_balance) || 0)
    + (project.periods || []).reduce((a, b) => a + (Number(b) || 0), 0);

  const allocated = useMemo(
    () => items.reduce((a, i) => a + (Number(i.amount) || 0), 0),
    [items]
  );
  const remainder = projectCost - allocated;
  const over = remainder < -0.005;

  const add = () => setItems((p) => [...p, {
    _key: `n${Math.random().toString(36).slice(2)}`,
    description: '', asset_class_code: '', amount: 0,
    useful_life_months: null, depreciation_method: null, asset_code: null,
  }]);

  const set = (key, field, value) =>
    setItems((p) => p.map((i) => (i._key === key ? { ...i, [field]: value } : i)));

  const classOf = (code) => assetClasses.find((c) => c.asset_class_code === code);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
        <div className="shrink-0 px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
              {project.capex_ref} · what this project creates
            </h2>
            <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {project.description}
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Allocation state, at the top, because it is the thing that decides
            whether this breakdown is usable. */}
        <div className={`shrink-0 px-5 py-2.5 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs ${
          over ? 'bg-red-50 dark:bg-red-500/10' : ''}`}>
          <Stat label="Project cost" value={fmt(projectCost)} />
          <Stat label="Itemised" value={fmt(allocated)} />
          <Stat
            label={over ? 'Over-allocated' : 'Remainder'}
            value={fmt(Math.abs(remainder))}
            tone={over ? 'bad' : remainder > 0 ? 'muted' : 'good'}
          />
          {over ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 dark:text-red-300">
              <TriangleAlert className="w-3.5 h-3.5" />
              The items cost more than the project — reduce one, or raise the phasing.
            </span>
          ) : remainder > 0 ? (
            <span className="text-[11px] text-slate-400">
              The remainder depreciates on the project&rsquo;s own class. Items are optional.
            </span>
          ) : (
            <span className="text-[11px] text-emerald-700 dark:text-emerald-400">
              Fully itemised — every rand depreciates on its own class.
            </span>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3">
          {items.length === 0 ? (
            <div className="py-8 text-center">
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
                Not broken down. The whole {fmt(projectCost)} depreciates on
                {project.asset_class_code ? ` ${project.asset_class_code}` : ' the project class'}.
              </p>
              <p className="m-0 mt-1 text-[11px] text-slate-400">
                Break it down where the project buys things with different lives &mdash; a 15-year line and a
                5-year vehicle should not depreciate together.
              </p>
              <button className={`${btn} mt-3`} onClick={add}>
                <Plus className="w-3.5 h-3.5" /> Add the first item
              </button>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pb-1.5 pr-2">Description</th>
                  <th className="pb-1.5 pr-2 w-40">Asset class</th>
                  <th className="pb-1.5 pr-2 w-28 text-right">Amount</th>
                  <th className="pb-1.5 pr-2 w-28">Life (mths)</th>
                  <th className="pb-1.5 pr-2 w-32">Asset code</th>
                  <th className="pb-1.5 w-8" />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const cls = classOf(i.asset_class_code);
                  return (
                    <tr key={i._key} className="border-t border-slate-100 dark:border-slate-700/60">
                      <td className="py-1.5 pr-2">
                        <input
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                          value={i.description || ''}
                          placeholder="What is being bought"
                          onChange={(e) => set(i._key, 'description', e.target.value)}
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <select
                          className="w-full px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                          value={i.asset_class_code || ''}
                          onChange={(e) => set(i._key, 'asset_class_code', e.target.value)}
                        >
                          <option value="">Project default</option>
                          {assetClasses.map((c) => (
                            <option key={c.asset_class_code} value={c.asset_class_code}>
                              {c.asset_class_code}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <input
                          type="number"
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-right text-slate-800 dark:text-slate-100"
                          value={i.amount ?? 0}
                          onChange={(e) => set(i._key, 'amount', e.target.value)}
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        {/* Blank inherits the class, shown as the placeholder so
                            the policy is visible without opening master data. */}
                        <input
                          type="number"
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                          value={i.useful_life_months ?? ''}
                          placeholder={cls?.default_useful_life_months
                            ? `${cls.default_useful_life_months} (class)` : 'class default'}
                          onChange={(e) => set(i._key, 'useful_life_months', e.target.value === '' ? null : e.target.value)}
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <input
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                          value={i.asset_code || ''}
                          placeholder="once capitalised"
                          title="Fill this in when the asset exists in the register. Blank until then — at budget time the thing has not been bought."
                          onChange={(e) => set(i._key, 'asset_code', e.target.value)}
                        />
                      </td>
                      <td className="py-1.5 text-right">
                        <button
                          onClick={() => setItems((p) => p.filter((x) => x._key !== i._key))}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {items.length > 0 && (
            <button className={`${btn} mt-3`} onClick={add}>
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          )}
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
          <p className="m-0 text-[11px] text-slate-400">
            Site and cost centre come from the project &mdash; an item cannot differ.
          </p>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={onClose}>Cancel</button>
            <button
              className={btnPrimary}
              disabled={over || items.some((i) => !String(i.description || '').trim())}
              title={over ? 'The items cost more than the project.'
                : items.some((i) => !String(i.description || '').trim()) ? 'Every item needs a description.' : ''}
              onClick={() => onSave(items.map((i) => ({
                description: i.description,
                asset_class_code: i.asset_class_code || null,
                amount: Number(i.amount) || 0,
                useful_life_months: i.useful_life_months === null || i.useful_life_months === ''
                  ? null : Number(i.useful_life_months),
                residual_value: i.residual_value ?? null,
                depreciation_method: i.depreciation_method || null,
                reducing_rate_pct: i.reducing_rate_pct ?? null,
                gl_asset_cost_account: i.gl_asset_cost_account || null,
                asset_code: i.asset_code || null,
                notes: i.notes || null,
              })))}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <span className={`font-mono font-bold ${
      tone === 'bad' ? 'text-red-700 dark:text-red-300'
        : tone === 'good' ? 'text-emerald-700 dark:text-emerald-400'
          : 'text-slate-800 dark:text-slate-100'}`}>{value}</span>
  </span>
);
